import { enableFormLoading } from "../../redux/actions";
import { APICore } from "./apiCore";
import { clearLocalStorage } from "./utils";
import { useEffect } from "react";
import { notification } from "antd";
import { controlOrMeta } from "lexical/LexicalUtils";
import auth from "../../auth";

const originalFetch = window.fetch;

// never settles, so dropped requests show no errors
const dropped = () => new Promise<Response>(() => {});

// a 401 from another host says nothing about our session
const isOwnRequest = (target: string) => {
  try {
    return (
      new URL(target, window.location.href).origin ===
      new URL(auth.apiUrl(), window.location.href).origin
    );
  } catch (e) {
    return false;
  }
};

window.fetch = (url, options = {}) => {
  if (auth.isRedirecting()) return dropped();

  const target =
    typeof url === "string" ? url : (url as Request).url || String(url);
  const headers = { ...auth.headers(), ...options.headers };
  const tracked = auth.trackRequest(options.signal);

  return originalFetch(url, { ...options, headers, signal: tracked.signal })
    .then((response) => {
      tracked.done();
      if (isOwnRequest(target)) {
        if (response.status === 401) {
          auth.onUnauthorized();
          return dropped();
        }
        auth.onSessionOk();
      }
      return response;
    })
    .catch((error) => {
      tracked.done();
      if (auth.isRedirecting()) return dropped();
      throw error;
    });
};

const api = new APICore();

const preBaseURL = `${auth.baseUrl()}/api/`;
if (!auth.baseUrl()) {
  window.location.href = "/";
}

// account
function login(params: { str_username: string; str_password: string }) {
  const baseUrl = `${preBaseURL}login`;

  return api.create(`${baseUrl}`, params);
}
function getUserDetails() {
  const apiUrl = `${preBaseURL}user/me`;
  return api.getUserDetails(apiUrl);
}
function logout() {
  return api.create(`${preBaseURL}logout`, {});
}
function signup(params: { fullname: string; email: string; password: string }) {
  const baseUrl = "/register/";
  return api.create(`${baseUrl}`, params);
}
function forgotPassword(params: { email: string }) {
  const baseUrl = "/forget-password/";
  return api.create(`${baseUrl}`, params);
}

const controllers: {
  shareWithListing: AbortController | null;
  fetchHDTickets:AbortController | null;
} = {
  shareWithListing: null,
  fetchHDTickets:null
};

async function requestTicketInputShareWithListing(payload: any) {
  const apiUrl = `${preBaseURL}search/user/by-id`;

  try {
    // Cancel any in-flight requests
    if (controllers.shareWithListing) {
      controllers.shareWithListing.abort();
    }

    // Create a new AbortController for this request
    controllers.shareWithListing = new AbortController();

    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
      signal: controllers.shareWithListing.signal, // Add signal to make request cancellable
    });

    if (response.status === 200) {
      const jsonData = await response.json();
      return jsonData;
    } else if (response.status === 401) {
      notification.error({
        message: "Unauthorized",
        description: "Your session has expired. Please log in again.",
      });
      auth.onUnauthorized();
    } else {
      notification.error({
        message: "Request Error",
        description: "Something went wrong. Please try again.",
      });
    }
  } catch (error: any) {
    // Ignore aborted requests
    if (error.name === "AbortError") {
      console.log("Request was cancelled");
      return;
    }

    console.error("Fetch error:", error);
    notification.error({
      message: "Network Error",
      description: "An error occurred while saving the ticket input.",
    });
    throw error;
  }
}

async function requestHelpDeskDropdown(payload: string, userId?: string) {
  let apiUrl = payload
    ? `${preBaseURL}helpdesk/?object_type=${payload}`
    : `${preBaseURL}helpdesk/`;

  // Add user_id parameter if provided

  if (userId) {
    const separator = apiUrl.includes("?") ? "&" : "?";
    apiUrl += `${separator}user_id=${userId}`;
  }
  try {
    const response = await fetch(apiUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });
    if (response.status === 401) {
      auth.onUnauthorized();
    }
    if (response.status === 200) {
      const jsonData = await response.json();
      return jsonData;
    } else if (response.status === 401) {
      notification.error({
        message: "Unauthorized",
        description: "Your session has expired. Please log in again.",
      });
      auth.onUnauthorized();
    } else {
      notification.error({
        message: "Request Error",
        description: "Something went wrong. Please try again.",
      });
    }
  } catch (error) {
    console.error("Fetch error:", error);
    notification.error({
      message: "Network Error",
      description: "An error occurred while saving the ticket input.",
    });
    throw error;
  }
}

async function helperRequestRelatedTicket(payload: any) {
  // const apiUrl = `${preBaseURL}search/content/title-by-id/2021527`;
  const apiUrl = `${preBaseURL}search/ticket/subject-by-id/${payload}`;
  try {
    const response = await fetch(apiUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (response.status === 200) {
      const jsonData = await response.json();
      return jsonData;
    } else if (response.status === 401) {
      notification.error({
        message: "Unauthorized",
        description: "Your session has expired. Please log in again.",
      });
      auth.onUnauthorized();
    } else {
      notification.error({
        message: "Request Error",
        description: "Something went wrong. Please try again.",
      });
    }
  } catch (error) {
    console.error("Fetch error:", error);
    notification.error({
      message: "Network Error",
      description: "An error occurred while saving the ticket input.",
    });
    throw error;
  }
}

async function helperRequestRelatedContent(payload: any) {
  const apiUrl = `${preBaseURL}search/content/title-by-id/${payload}`;

  try {
    const response = await fetch(apiUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (response.status === 200) {
      const jsonData = await response.json();
      return jsonData;
    } else if (response.status === 401) {
      notification.error({
        message: "Unauthorized",
        description: "Your session has expired. Please log in again.",
      });
      auth.onUnauthorized();
    } else {
      notification.error({
        message: "Request Error",
        description: "Something went wrong. Please try again.",
      });
    }
  } catch (error) {
    console.error("Fetch error:", error);
    notification.error({
      message: "Network Error",
      description: "An error occurred while saving the ticket input.",
    });
    throw error;
  }
}

async function helperRequestSearchContentByTitle(payload: any) {
  const apiUrl = `${preBaseURL}search/content/by-title`;

  try {
    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (response.status === 200) {
      const jsonData = await response.json();
      return jsonData;
    } else if (response.status === 401) {
      notification.error({
        message: "Unauthorized",
        description: "Your session has expired. Please log in again.",
      });
      auth.onUnauthorized();
    } else {
      notification.error({
        message: "Request Error",
        description: "Something went wrong. Please try again.",
      });
    }
  } catch (error) {
    console.error("Fetch error:", error);
    notification.error({
      message: "Network Error",
      description: "An error occurred while saving the ticket input.",
    });
    throw error;
  }
}

async function helperRequestSearchTicketBySubject(payload: any) {
  const apiUrl = `${preBaseURL}search/ticket/by-subject`;
  try {
    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (response.status === 200) {
      const jsonData = await response.json();
      return jsonData;
    } else if (response.status === 401) {
      notification.error({
        message: "Unauthorized",
        description: "Your session has expired. Please log in again.",
      });
      auth.onUnauthorized();
    } else {
      notification.error({
        message: "Request Error",
        description: "Something went wrong. Please try again.",
      });
    }
  } catch (error) {
    console.error("Fetch error:", error);
    notification.error({
      message: "Network Error",
      description: "An error occurred while saving the ticket input.",
    });
    throw error;
  }
}

async function helperRequestClassificationNode(payload: any) {
  const apiUrl = payload
    ? `${preBaseURL}classification/sub-nodes/${payload}`
    : `${preBaseURL}classification/`;

  try {
    const response = await fetch(apiUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (response.status === 200) {
      const jsonData = await response.json();
      return jsonData;
    } else if (response.status === 401) {
      notification.error({
        message: "Unauthorized",
        description: "Your session has expired. Please log in again.",
      });
      auth.onUnauthorized();
    } else {
      notification.error({
        message: "Request Error",
        description: "Something went wrong. Please try again.",
      });
    }
  } catch (error) {
    console.error("Fetch error:", error);
    notification.error({
      message: "Network Error",
      description: "An error occurred while saving the ticket input.",
    });
    throw error;
  }
}

async function helperRequestSearchTaxoByName(payload: any) {
  const apiUrl = `${preBaseURL}classification/by-name/${payload}`;
  try {
    const response = await fetch(apiUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (response.status === 200) {
      const jsonData = await response.json();
      return jsonData;
    } else if (response.status === 401) {
      notification.error({
        message: "Unauthorized",
        description: "Your session has expired. Please log in again.",
      });
      auth.onUnauthorized();
    } else {
      notification.error({
        message: "Request Error",
        description: "Something went wrong. Please try again.",
      });
    }
  } catch (error) {
    console.error("Fetch error:", error);
    notification.error({
      message: "Network Error",
      description: "An error occurred while saving the ticket input.",
    });
    throw error;
  }
}

async function helperRequestFavoriteTaxo(payload: any) {
  const apiUrl = `${preBaseURL}classification/favorite`;
  try {
    const response = await fetch(apiUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (response.status === 200) {
      const jsonData = await response.json();
      return jsonData;
    } else if (response.status === 401) {
      notification.error({
        message: "Unauthorized",
        description: "Your session has expired. Please log in again.",
      });
      auth.onUnauthorized();
    } else {
      notification.error({
        message: "Request Error",
        description: "Something went wrong. Please try again.",
      });
    }
  } catch (error) {
    console.error("Fetch error:", error);
    notification.error({
      message: "Network Error",
      description: "An error occurred while saving the ticket input.",
    });
    throw error;
  }
}

async function helperRequestAddFavoriteTaxo(payload: any) {
  const apiUrl = `${preBaseURL}classification/favorite`;
  try {
    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (response.status === 200) {
      const jsonData = await response.json();
      return jsonData;
    } else if (response.status === 401) {
      notification.error({
        message: "Unauthorized",
        description: "Your session has expired. Please log in again.",
      });
      auth.onUnauthorized();
    } else {
      notification.error({
        message: "Request Error",
        description: "Something went wrong. Please try again.",
      });
    }
  } catch (error) {
    console.error("Fetch error:", error);
    notification.error({
      message: "Network Error",
      description: "An error occurred while saving the ticket input.",
    });
    throw error;
  }
}

async function helperRequestRemoveFavoriteTaxo(payload: any) {
  const apiUrl = `${preBaseURL}classification/favorite/${payload}`;
  try {
    const response = await fetch(apiUrl, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (response.status === 200) {
      const jsonData = await response.json();
      return jsonData;
    } else if (response.status === 401) {
      notification.error({
        message: "Unauthorized",
        description: "Your session has expired. Please log in again.",
      });
      auth.onUnauthorized();
    } else {
      notification.error({
        message: "Request Error",
        description: "Something went wrong. Please try again.",
      });
    }
  } catch (error) {
    console.error("Fetch error:", error);
    notification.error({
      message: "Network Error",
      description: "An error occurred while saving the ticket input.",
    });
    throw error;
  }
}

async function helperRequestMyTicketListing(payload: any) {
  const apiUrl = `${preBaseURL}search/ticket/my`;
  if(controllers.fetchHDTickets){
    controllers.fetchHDTickets.abort()
  }
  controllers.fetchHDTickets = new AbortController()
  try {
    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
      signal:controllers.fetchHDTickets.signal
    });

    if (response.status === 200) {
      const jsonData = await response.json();
      return jsonData;
    } else if (response.status === 401) {
      notification.error({
        message: "Unauthorized",
        description: "Your session has expired. Please log in again.",
      });
      auth.onUnauthorized();
    } else {
      notification.error({
        message: "Request Error",
        description: "Something went wrong. Please try again.",
      });
    }
  } catch (error:any) {
    if(error.name === "AbortError") {
      console.log("Request was cancelled");
      return;
    }
    console.error("Fetch error:", error);
    notification.error({
      message: "Network Error",
      description: "An error occurred while saving the ticket input.",
    });
    throw error;
  }
}

async function helperRequestMyContentListing(payload: any) {
  if(controllers.fetchHDTickets){
    controllers.fetchHDTickets.abort()
  }
  controllers.fetchHDTickets = new AbortController()

  const apiUrl = `${preBaseURL}search/content/my`;
  try {
    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
      signal:controllers.fetchHDTickets.signal
    });

    if (response.status === 200) {
      const jsonData = await response.json();
      return jsonData;
    } else if (response.status === 401) {
      notification.error({
        message: "Unauthorized",
        description: "Your session has expired. Please log in again.",
      });
      auth.onUnauthorized();
    } else {
      notification.error({
        message: "Request Error",
        description: "Something went wrong. Please try again.",
      });
    }
  } catch (error:any) {
    if (error.name === "AbortError") {
      console.log("Request was cancelled");
      return;
    }
    console.error("Fetch error:", error);
    notification.error({
      message: "Network Error",
      description: "An error occurred while saving the ticket input.",
    });
    throw error;
  }
}

async function helperRequestHDTicketListing(payload: any) {
  //working
  const apiUrl = `${preBaseURL}search/ticket/hd`;
  try {
    if (controllers.fetchHDTickets) {
        controllers.fetchHDTickets.abort();
    }

    controllers.fetchHDTickets = new AbortController()

    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
      signal:controllers.fetchHDTickets.signal
    });

    if (response.status === 200) {
      const jsonData = await response.json();
      return jsonData;
    } else if (response.status === 401) {
      notification.error({
        message: "Unauthorized",
        description: "Your session has expired. Please log in again.",
      });
      auth.onUnauthorized();
    } else {
      notification.error({
        message: "Request Error",
        description: "Something went wrong. Please try again.",
      });
    }
  } catch (error:any) {
    console.error("Fetch error:", error);
    if (error.name === "AbortError") {
      console.log("Request was cancelled");
      return;
    }
    notification.error({
      message: "Network Error",
      description: "An error occurred while saving the ticket input.",
    });
    throw error;
  }
}

async function helperRequestHDContentListing(payload: any) {
  const apiUrl = `${preBaseURL}search/content/hd`;
  if(controllers.fetchHDTickets){
    controllers.fetchHDTickets.abort()
  }

  controllers.fetchHDTickets = new AbortController()
  try {
    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
      signal:controllers.fetchHDTickets.signal
    });

    if (response.status === 200) {
      const jsonData = await response.json();
      return jsonData;
    } else if (response.status === 401) {
      notification.error({
        message: "Unauthorized",
        description: "Your session has expired. Please log in again.",
      });
      auth.onUnauthorized();
    } else {
      notification.error({
        message: "Request Error",
        description: "Something went wrong. Please try again.",
      });
    }
  } catch (error:any) {
      console.error("Fetch error:", error);
    if (error.name === "AbortError") {
      console.log("Request was cancelled");
      return;
    }
    console.error("Fetch error:", error);
    notification.error({
      message: "Network Error",
      description: "An error occurred while saving the ticket input.",
    });
    throw error;
  }
}

// async function helperRequestSaveTicketInput(payload: any) {
//   const apiUrl = `${preBaseURL}ticket/`;
//   try {
//     const response = await fetch(apiUrl, {
//       method: "POST",
//       headers: {
//
//         "Content-Type": "application/json",
//       },
//       body: JSON.stringify(payload),
//     });

//     const jsonData = await response.json();
//     return jsonData;
//   } catch (error) {
//     console.error("Fetch error:", error);
//   }
// }

async function helperRequestSaveTicketInput(payload: any) {
  const apiUrl = `${preBaseURL}ticket/`;
  try {
    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (response.status === 200) {
      const jsonData = await response.json();
      return { statusCode: response.status, data: jsonData };
    } else if (response.status === 401) {
      notification.error({
        message: "Unauthorized",
        description: "Your session has expired. Please log in again.",
      });
      auth.onUnauthorized();
    } else {
      notification.error({
        message: "Request Error",
        description: "Something went wrong. Please try again.",
      });
    }
  } catch (error) {
    console.error("Fetch error:", error);
    notification.error({
      message: "Network Error",
      description: "An error occurred while saving the ticket input.",
    });
    throw error;
  }
}

async function helperRequestSaveContentInput(payload: any) {
  const apiUrl = `${preBaseURL}content/`;
  try {
    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (response.status === 200) {
      const jsonData = await response.json();
      return jsonData;
    } else if (response.status === 401) {
      notification.error({
        message: "Unauthorized",
        description: "Your session has expired. Please log in again.",
      });
      auth.onUnauthorized();
    } else {
      notification.error({
        message: "Request Error",
        description: "Something went wrong. Please try again.",
      });
    }
  } catch (error) {
    console.error("Fetch error:", error);
    notification.error({
      message: "Network Error",
      description: "An error occurred while saving the content input.",
    });
  }
}

async function helperRequestStatusFilter(payload: any) {
  const apiUrl = `${preBaseURL}status/?status_for=${payload}`;
  try {
    const response = await fetch(apiUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (response.status === 200) {
      const jsonData = await response.json();
      return jsonData;
    } else if (response.status === 401) {
      notification.error({
        message: "Unauthorized",
        description: "Your session has expired. Please log in again.",
      });
      auth.onUnauthorized();
    } else {
      notification.error({
        message: "Request Error",
        description: "Something went wrong. Please try again.",
      });
    }
  } catch (error) {
    console.error("Fetch error:", error);
    notification.error({
      message: "Network Error",
      description: "An error occurred while filtering the status.",
    });
  }
}

async function helperRequestContentTypeFilter() {
  const apiUrl = `${preBaseURL}content-type/`;
  try {
    const response = await fetch(apiUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (response.status === 200) {
      const jsonData = await response.json();
      return jsonData;
    } else if (response.status === 401) {
      notification.error({
        message: "Unauthorized",
        description: "Your session has expired. Please log in again.",
      });
      auth.onUnauthorized();
    } else {
      notification.error({
        message: "Request Error",
        description: "Something went wrong. Please try again.",
      });
    }
  } catch (error) {
    console.error("Fetch error:", error);
    notification.error({
      message: "Network Error",
      description: "An error occurred while fetching the content types.",
    });
  }
}

async function helperRequestPriorityDropdownList() {
  const apiUrl = `${preBaseURL}priority/`;
  try {
    const response = await fetch(apiUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (response.status === 200) {
      const jsonData = await response.json();
      return jsonData;
    } else if (response.status === 401) {
      notification.error({
        message: "Unauthorized",
        description: "Your session has expired. Please log in again.",
      });
      auth.onUnauthorized();
    } else {
      notification.error({
        message: "Request Error",
        description: "Something went wrong. Please try again.",
      });
    }
  } catch (error) {
    console.error("Fetch error:", error);
    notification.error({
      message: "Network Error",
      description:
        "An error occurred while fetching the priority dropdown list.",
    });
  }
}

async function helperRequestCompanyDropdownList() {
  const apiUrl = `${preBaseURL}company/`;
  try {
    const response = await fetch(apiUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (response.status === 200) {
      const jsonData = await response.json();
      return jsonData;
    } else if (response.status === 401) {
      notification.error({
        message: "Unauthorized",
        description: "Your session has expired. Please log in again.",
      });
      auth.onUnauthorized();
    } else {
      // Handle other status codes
      notification.error({
        message: "Request Error",
        description: "Something went wrong. Please try again.",
      });
    }
  } catch (error) {
    console.error("Fetch error:", error);
    notification.error({
      message: "Network Error",
      description: "An error occurred while fetching data.",
    });
  }
}

async function APIRequest(url: string, method: string, body?: any) {
  const apiUrl = `${preBaseURL}${url}`;
  if (auth.baseUrl()) {
    const headers = {
      "Content-Type": "application/json",
    };

    const requestOptions: RequestInit = {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    };

    try {
      const response = await fetch(apiUrl, requestOptions);
      if (response.status === 401) {
        auth.onUnauthorized();
      } else if (response.status === 200) {
        const jsonData = await response.json();
        return jsonData;
      } else if (response.status === 409) {
        notification.info({
          message: "Duplication !",
          description:
            "This element has already been added. Please select a different one.",
        });
      } else {
        // notification.error({
        //   message: "Request Error",
        //   description: "Something went wrong. Please try again.",
        // });
      }
    } catch (error) {
      console.error("Fetch error:", error);
      // notification.error({
      //   message: "Request Error",
      //   description: "Something went wrong. Please try again.",
      // });
    }
  }
}

export {
  APIRequest,
  helperRequestCompanyDropdownList,
  helperRequestPriorityDropdownList,
  helperRequestContentTypeFilter,
  helperRequestStatusFilter,
  login,
  logout,
  signup,
  forgotPassword,
  getUserDetails,
  requestTicketInputShareWithListing,
  requestHelpDeskDropdown,
  helperRequestRelatedTicket,
  helperRequestRelatedContent,
  helperRequestSearchContentByTitle,
  helperRequestSearchTicketBySubject,
  helperRequestClassificationNode,
  helperRequestSearchTaxoByName,
  helperRequestFavoriteTaxo,
  helperRequestAddFavoriteTaxo,
  helperRequestRemoveFavoriteTaxo,
  helperRequestMyTicketListing,
  helperRequestMyContentListing,
  helperRequestSaveTicketInput,
  helperRequestSaveContentInput,
  helperRequestHDTicketListing,
  helperRequestHDContentListing,
};


